////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// 🟢 [Subsection 21: BRIGHT GREEN] III. IMPORTS - Rob, importing React FC type!
import { FC, CSSProperties, useState, useEffect } from 'react';

// 🔵 [Subsection 30: BRIGHT BLUE] Rob, TypeScript interface for security component props!
interface SecurityProps {
  activeSection: string;
  securityContent: Record<string, { title: string; content: string }>;
  onSectionChange: (section: string) => void;
  onPrint: () => void;
  onDownload: () => void;
  style?: CSSProperties;
  className?: string;
}

// 🔵 [Subsection 32: SKY BLUE] Rob, SECTION_ICONS maps security sections to visual emojis!
const SECTION_ICONS: Record<string, string> = {
  overview: "🛡️",
  compliance: "✓",
  infrastructure: "☁️",
  "data-protection": "🔒",
  "access-control": "🔑",
  privacy: "👁️",
  contact: "✉️",
};

// 🔵 [Subsection 45: INDIGO] V. FUNCTION/CLASS NAME - Rob, main Pea_Security function!
const Pea_Security: FC<SecurityProps> = ({
  activeSection,
  securityContent,
  onSectionChange,
  onPrint,
  onDownload,
}) => {
  // 🩷 [Subsection 62: HOT PINK] Rob, currentContent uses activeSection from props!
  const currentContent = securityContent[activeSection] || securityContent.overview;

  // T_MOBILE_RESPONSIVE_2026_01_02: Mobile detection and sidebar toggle
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ⚫ [Subsection 91: OBSIDIAN] X. RENDER - Rob, returning complete JSX structure!
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {/* Header */}
      <header style={{
        padding: isMobile ? '16px 20px' : '24px 48px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap' as const,
        gap: '12px',
        background: 'linear-gradient(180deg, rgba(32,178,170,0.08) 0%, transparent 100%)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="https://www.bitsbound.com/favicon.svg"
              alt="BitsBound"
              style={{ height: '40px' }}
            />
          </a>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 600, margin: 0, color: '#20b2aa' }}>Security</h1>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', margin: '4px 0 0 0' }}>
              Enterprise-grade protection for your documents
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onPrint}
            style={{
              padding: '10px 20px',
              backgroundColor: 'transparent',
              border: '1px solid rgba(32,178,170,0.4)',
              borderRadius: '10px',
              color: '#20b2aa',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(32,178,170,0.1)';
              e.currentTarget.style.borderColor = '#20b2aa';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(32,178,170,0.4)';
            }}
          >
            Print
          </button>
          <button
            onClick={onDownload}
            style={{
              padding: '10px 20px',
              backgroundColor: 'rgba(32,178,170,0.15)',
              border: '1px solid #20b2aa',
              borderRadius: '10px',
              color: '#20b2aa',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(32,178,170,0.25)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(32,178,170,0.15)';
            }}
          >
            Download PDF
          </button>
        </div>
      </header>

      {/* Mobile Menu Toggle */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '16px 20px 0',
            padding: '12px 16px',
            backgroundColor: 'rgba(32,178,170,0.1)',
            border: '1px solid rgba(32,178,170,0.3)',
            borderRadius: '10px',
            color: '#20b2aa',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: '18px' }}>{sidebarOpen ? '✕' : '☰'}</span>
          <span>{sidebarOpen ? 'Close Menu' : 'View Sections'}</span>
        </button>
      )}

      {/* Main Content */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' as const : 'row' as const,
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? '20px 16px' : '40px',
        gap: isMobile ? '24px' : '48px',
      }}>
        {/* Sidebar Navigation */}
        <nav style={{
          width: isMobile ? '100%' : '300px',
          flexShrink: 0,
          display: isMobile && !sidebarOpen ? 'none' : 'block',
        }}>
          <div style={{
            position: 'sticky',
            top: '32px',
            backgroundColor: 'rgba(255,255,255,0.02)',
            borderRadius: '20px',
            padding: '20px',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            backdropFilter: 'blur(10px)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}>
              <span style={{ fontSize: '20px' }}>📑</span>
              <h3 style={{
                fontSize: '14px',
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                color: 'rgba(255,255,255,0.6)',
                margin: 0,
                fontWeight: 600,
              }}>
                Sections
              </h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {Object.entries(securityContent).map(([key, section]) => (
                <button
                  key={key}
                  onClick={() => onSectionChange(key)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    textAlign: 'left',
                    backgroundColor: activeSection === key ? 'rgba(32, 178, 170, 0.15)' : 'transparent',
                    border: activeSection === key ? '1px solid rgba(32, 178, 170, 0.3)' : '1px solid transparent',
                    borderRadius: '12px',
                    color: activeSection === key ? '#20b2aa' : 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: activeSection === key ? 600 : 400,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseOver={(e) => {
                    if (activeSection !== key) {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                      e.currentTarget.style.color = '#ffffff';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (activeSection !== key) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                    }
                  }}
                >
                  <span style={{ fontSize: '16px', width: '24px', textAlign: 'center' }}>
                    {SECTION_ICONS[key] || '📄'}
                  </span>
                  <span style={{ flex: 1 }}>{section.title}</span>
                  {activeSection === key && (
                    <span style={{ color: '#20b2aa', fontSize: '12px' }}>●</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </nav>

        {/* Content Area */}
        <article style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.02)',
            borderRadius: isMobile ? '16px' : '20px',
            padding: isMobile ? '20px' : '40px',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            backdropFilter: 'blur(10px)',
          }}>
            {/* Section Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '32px',
              paddingBottom: '24px',
              borderBottom: '1px solid rgba(32,178,170,0.2)',
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(32,178,170,0.2) 0%, rgba(32,178,170,0.05) 100%)',
                border: '1px solid rgba(32,178,170,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
              }}>
                {SECTION_ICONS[activeSection] || '📄'}
              </div>
              <div>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: 700,
                  margin: 0,
                  color: '#ffffff',
                }}>
                  {currentContent.title}
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.5)',
                  margin: '4px 0 0 0',
                }}>
                  BitsBound Security
                </p>
              </div>
            </div>
            {/* Section Content */}
            <div style={{
              lineHeight: 1.8,
              color: 'rgba(255,255,255,0.85)',
              fontSize: '15px',
            }}>
              {currentContent.content.split("\n\n").map((paragraph, index) => {
                if (paragraph.includes("•")) {
                  const lines = paragraph.split("\n");
                  const title = lines[0].endsWith(":") ? lines[0] : null;
                  const bulletPoints = lines.filter(line => line.trim().startsWith("•"));

                  if (bulletPoints.length > 0) {
                    return (
                      <div key={index} style={{ marginBottom: '28px' }}>
                        {title && (
                          <h3 style={{
                            fontWeight: 600,
                            color: '#20b2aa',
                            marginBottom: '16px',
                            fontSize: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}>
                            <span style={{
                              width: '4px',
                              height: '16px',
                              backgroundColor: '#20b2aa',
                              borderRadius: '2px',
                            }} />
                            {title}
                          </h3>
                        )}
                        <ul style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                        }}>
                          {bulletPoints.map((point, i) => (
                            <li
                              key={i}
                              style={{
                                padding: '12px 16px 12px 40px',
                                position: 'relative',
                                backgroundColor: 'rgba(255,255,255,0.02)',
                                borderRadius: '10px',
                                border: '1px solid rgba(255,255,255,0.05)',
                              }}
                            >
                              <span style={{
                                position: 'absolute',
                                left: '16px',
                                top: '14px',
                                color: '#20b2aa',
                                fontWeight: 'bold',
                              }}>
                                •
                              </span>
                              {point.replace(/^[•\s]+/, "").trim()}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                }
                return (
                  <p key={index} style={{
                    marginBottom: '20px',
                    color: 'rgba(255,255,255,0.85)',
                  }}>
                    {paragraph}
                  </p>
                );
              })}
            </div>
          </div>

          {/* Quick Navigation Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '24px',
            padding: '20px',
            backgroundColor: 'rgba(255,255,255,0.02)',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.05)',
          }}>
            {(() => {
              const sections = Object.keys(securityContent);
              const currentIndex = sections.indexOf(activeSection);
              const prevSection = currentIndex > 0 ? sections[currentIndex - 1] : null;
              const nextSection = currentIndex < sections.length - 1 ? sections[currentIndex + 1] : null;

              return (
                <>
                  {prevSection ? (
                    <button
                      onClick={() => onSectionChange(prevSection)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        backgroundColor: 'transparent',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        color: 'rgba(255,255,255,0.7)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(32,178,170,0.5)';
                        e.currentTarget.style.color = '#20b2aa';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                        e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                      }}
                    >
                      <span>←</span>
                      <span>{securityContent[prevSection].title}</span>
                    </button>
                  ) : <div />}
                  {nextSection && (
                    <button
                      onClick={() => onSectionChange(nextSection)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        backgroundColor: 'rgba(32,178,170,0.1)',
                        border: '1px solid rgba(32,178,170,0.3)',
                        borderRadius: '10px',
                        color: '#20b2aa',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(32,178,170,0.2)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(32,178,170,0.1)';
                      }}
                    >
                      <span>{securityContent[nextSection].title}</span>
                      <span>→</span>
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        </article>
      </div>

      {/* Footer */}
      <footer style={{
        padding: isMobile ? '24px 16px' : '40px 48px',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        textAlign: 'center',
        background: 'linear-gradient(0deg, rgba(32,178,170,0.05) 0%, transparent 100%)',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap' as const,
            gap: isMobile ? '12px' : '24px',
            marginBottom: '20px',
          }}>
            <a href="/terms-of-service" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>
              Terms of Service
            </a>
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
            <a href="/privacy" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>
              Privacy Policy
            </a>
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
            <a href="/dpa" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>
              DPA
            </a>
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
            <a href="/" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: '14px' }}>
              Home
            </a>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', margin: 0 }}>
            © 2025 BitsBound, Inc. (A Taytrom Company). All rights reserved.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '8px' }}>
            Last updated: December 21, 2025
          </p>
        </div>
      </footer>
    </div>
  );
};

// ⚫ [Subsection 95: ONYX] XI. EXPORTS
export default Pea_Security;